// NaverWorks SSO Authentication Utility

const AUTH_CONFIG = {
  clientId: process.env.REACT_APP_NAVER_WORKS_CLIENT_ID,
  redirectUri: process.env.REACT_APP_NAVER_WORKS_REDIRECT_URI,
  authUrl: process.env.REACT_APP_NAVER_WORKS_AUTH_URL,
  tokenUrl: process.env.REACT_APP_NAVER_WORKS_TOKEN_URL,
  userInfoUrl: process.env.REACT_APP_NAVER_WORKS_USERINFO_URL,
  scope: 'user.read',
};

/**
 * Generate OAuth 2.0 authorization URL for NaverWorks SSO
 */
export const getAuthorizationUrl = () => {
  const state = generateRandomState();
  sessionStorage.setItem('oauth_state', state);

  const params = new URLSearchParams({
    client_id: AUTH_CONFIG.clientId,
    redirect_uri: AUTH_CONFIG.redirectUri,
    response_type: 'code',
    scope: AUTH_CONFIG.scope,
    state: state,
  });

  return `${AUTH_CONFIG.authUrl}?${params.toString()}`;
};

/**
 * Generate random state for CSRF protection
 */
const generateRandomState = () => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Validate OAuth state parameter
 */
export const validateState = (state) => {
  const savedState = sessionStorage.getItem('oauth_state');
  sessionStorage.removeItem('oauth_state');
  return state === savedState;
};

/**
 * Exchange authorization code for access token via Lambda
 */
export const exchangeCodeForToken = async (code) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!apiBaseUrl) {
    throw new Error('API base URL not configured');
  }

  const response = await fetch(`${apiBaseUrl}/api/auth/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to exchange code for token');
  }

  const data = await response.json();
  return data.data; // Extract data from success response
};

/**
 * Get user information using access token via Lambda
 */
export const getUserInfo = async (accessToken) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  if (!apiBaseUrl) {
    throw new Error('API base URL not configured');
  }

  const response = await fetch(`${apiBaseUrl}/api/auth/userinfo`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to get user information');
  }

  const data = await response.json();
  return data.data; // Extract data from success response
};

/**
 * Store authentication data in localStorage
 */
export const storeAuthData = (authData) => {
  localStorage.setItem('auth_token', authData.access_token);
  localStorage.setItem('auth_user', JSON.stringify(authData.user));

  if (authData.refresh_token) {
    localStorage.setItem('refresh_token', authData.refresh_token);
  }
};

/**
 * Retrieve authentication data from localStorage
 */
export const getAuthData = () => {
  const token = localStorage.getItem('auth_token');
  const userStr = localStorage.getItem('auth_user');

  if (!token || !userStr) {
    return null;
  }

  try {
    const user = JSON.parse(userStr);
    return { token, user };
  } catch (error) {
    console.error('Failed to parse user data:', error);
    return null;
  }
};

/**
 * Clear authentication data
 */
export const clearAuthData = () => {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('auth_user');
  localStorage.removeItem('refresh_token');
  sessionStorage.removeItem('oauth_state');
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  const authData = getAuthData();
  return authData !== null && authData.token !== null;
};

/**
 * Logout and redirect to login
 */
export const logout = () => {
  clearAuthData();
  window.location.href = '/login';
};

/**
 * Append data to Google Sheets via Lambda
 */
export const appendToGoogleSheets = async (userName, orgUnitName = '') => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const authData = getAuthData();

  if (!apiBaseUrl) {
    throw new Error('API base URL not configured');
  }

  if (!authData || !authData.token) {
    throw new Error('User not authenticated');
  }

  // Create timestamp and date in Korean timezone
  const now = new Date();
  const timestamp = now.toLocaleTimeString('ko-KR', {
    timeZone: 'Asia/Seoul',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
  const date = now.toLocaleDateString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  const response = await fetch(`${apiBaseUrl}/api/sheets/append`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${authData.token}`,
    },
    body: JSON.stringify({
      userName,
      timestamp,
      date,
      orgUnitName,
    }),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch (parseError) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const errorMessage = errorData.error || 'Failed to append data to Google Sheets';
    const errorDetails = errorData.details ? `\nDetails: ${JSON.stringify(errorData.details, null, 2)}` : '';
    throw new Error(`${errorMessage}${errorDetails}`);
  }

  const data = await response.json();
  return data.data; // Extract data from success response
};

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환 (한국 시간 기준)
 */
export const getTodayDate = () => {
  const now = new Date();
  return now.toLocaleDateString('ko-KR', {
    timeZone: 'Asia/Seoul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).replace(/\. /g, '-').replace('.', '');
};

/**
 * 오늘 이미 기록했는지 확인
 */
export const hasRecordedToday = (userId) => {
  const today = getTodayDate();
  const recordKey = `meal_record_${userId}_${today}`;
  return localStorage.getItem(recordKey) !== null;
};

/**
 * 오늘 기록 저장
 */
export const saveTodayRecord = (userId, recordData) => {
  const today = getTodayDate();
  const recordKey = `meal_record_${userId}_${today}`;
  localStorage.setItem(recordKey, JSON.stringify({
    date: today,
    timestamp: new Date().toISOString(),
    ...recordData
  }));
};

/**
 * 오늘 기록 가져오기
 */
export const getTodayRecord = (userId) => {
  const today = getTodayDate();
  const recordKey = `meal_record_${userId}_${today}`;
  const record = localStorage.getItem(recordKey);
  return record ? JSON.parse(record) : null;
};

/**
 * 오래된 기록 정리 (7일 이상 된 기록 삭제)
 */
export const cleanupOldRecords = (userId) => {
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(`meal_record_${userId}_`)) {
      const dateStr = key.split('_')[3];
      const recordDate = new Date(dateStr);

      if (recordDate < sevenDaysAgo) {
        localStorage.removeItem(key);
      }
    }
  });
};
